import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  MapPin,
  Building2,
  Sparkles,
  ShieldCheck,
  ChevronRight,
  Star,
  ArrowRight,
  X,
  Maximize2,
  Phone,
  Check,
  Mail
} from "lucide-react";
import { COMPANY_INFO, SERVICES, ADVANTAGES, WHY_CHOOSE_US, REVIEWS } from "../data";
import ContactForm from "../components/ContactForm";

export default function Home() {
  const [galleryItems, setGalleryItems] = useState([]);
  const [loadingGallery, setLoadingGallery] = useState(true);
  const [lightboxImage, setLightboxImage] = useState(null);

  // Fetch gallery preview from CMS
  useEffect(() => {
    async function fetchGallery() {
      try {
        const response = await fetch("https://api.dev.auxo.dk/public/content/gallery");
        if (response.ok) {
          const data = await response.json();
          // Take first 8 items for a perfect 4-column or 8-column showcase
          setGalleryItems(data.slice(0, 8));
        }
      } catch (error) {
        console.error("Fejl ved hentning af galleri:", error);
      } finally {
        setLoadingGallery(false);
      }
    }
    fetchGallery();
  }, []);

  // Map icon name to Lucide component
  const getIconComponent = (iconName) => {
    switch (iconName) {
      case "MapPin":
        return <MapPin className="h-6 w-6" />;
      case "Building2":
        return <Building2 className="h-6 w-6" />;
      case "Sparkles":
        return <Sparkles className="h-6 w-6" />;
      case "ShieldCheck":
        return <ShieldCheck className="h-6 w-6" />;
      default:
        return <Sparkles className="h-6 w-6" />;
    }
  };

  return (
    <div className="pt-[72px] sm:pt-[128px]">
      {/* Hero Section */}
      <section className="relative bg-slate-950 pt-24 pb-32 sm:pt-32 sm:pb-44 flex flex-col items-center justify-center">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/66f352d5-c4b9-4e8b-8d46-eb7bb9ac1d49/image00037.png"
            alt="Skræp Vinduespuds i arbejde"
            className="w-full h-full object-cover opacity-55"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/70 via-slate-950/50 to-slate-950/75" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold bg-sky-500/10 text-sky-400 border border-sky-500/20 mb-6 uppercase tracking-wider">
            <MapPin className="h-3.5 w-3.5" /> Professionel service på {COMPANY_INFO.geoArea}
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-white tracking-normal leading-[1.15] mb-6 normal-case">
            Krystalklare ruder &amp; <span className="text-sky-400">professionel</span> ejendomsservice
          </h1>
          <p className="text-lg sm:text-xl text-slate-300 mb-10 max-w-3xl mx-auto leading-relaxed">
            Skræp Vinduespuds leverer stribefri vinduespudsning, solcellerens, algerens og rengøring af campingvogne til private og erhverv på hele Fyn.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/kontakt"
              className="w-full sm:w-auto bg-sky-500 hover:bg-sky-600 text-white px-8 py-4 rounded-md text-sm font-bold uppercase tracking-wider transition-all shadow-sm"
            >
              Få et gratis tilbud
            </Link>
            <a
              href={`tel:${COMPANY_INFO.phone.replace(/\s+/g, "")}`}
              className="w-full sm:w-auto border border-white hover:bg-white/10 text-white font-bold px-8 py-4 rounded-md transition-all text-sm tracking-wide uppercase"
            >
              Ring os op
            </a>
          </div>
        </div>
      </section>

      {/* Services Multi-Grid (Overlapping the Hero Section) */}
      <div className="relative z-20 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-20 sm:-mt-32 mb-16">
        {/* Row 1: 2 Columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
          {SERVICES.slice(0, 3).map((service) => (
            <Link
              key={service.id}
              to={`/ydelser/${service.slug}`}
              className={`relative group overflow-hidden rounded-md aspect-[4/3] cursor-pointer shadow-xl border border-slate-200 bg-slate-900 ${
                service.slug === "solcellerens" ? "md:col-span-2 md:aspect-[2.2/1] lg:col-span-1 lg:aspect-[4/3]" : ""
              }`}
            >
              <img
                src={service.slug === "vinduespudsning-privat" ? "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/66f352d5-c4b9-4e8b-8d46-eb7bb9ac1d49/shutterstock-2622574991-1.jpg" : service.image}
                className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105 opacity-80"
                alt={service.title}
              />
              <div className="absolute inset-0 bg-slate-950/60 group-hover:bg-slate-950/75 transition-all duration-300 flex flex-col justify-end p-6">
                <h3 className="text-white text-xl font-bold border-b border-white/20 pb-4 mb-2 uppercase tracking-wide">
                  {service.title}
                </h3>
                <p className="text-slate-300 text-xs line-clamp-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 mb-2">
                  {service.shortDescription}
                </p>
                <span className="text-sky-400 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1 group-hover:text-sky-300">
                  Læs mere <ChevronRight className="h-3 w-3" />
                </span>
              </div>
            </Link>
          ))}
        </div>

        {/* Row 2: 3 Columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.slice(3, 6).map((service) => (
            <Link
              key={service.id}
              to={`/ydelser/${service.slug}`}
              className="relative group overflow-hidden rounded-md aspect-[4/3] cursor-pointer shadow-xl border border-slate-200 bg-slate-900"
            >
              <img
                src={service.image}
                className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105 opacity-80"
                alt={service.title}
              />
              <div className="absolute inset-0 bg-slate-950/60 group-hover:bg-slate-950/75 transition-all duration-300 flex flex-col justify-end p-6">
                <h3 className="text-white text-xl font-bold border-b border-white/20 pb-4 mb-2 uppercase tracking-wide">
                  {service.title}
                </h3>
                <p className="text-slate-300 text-xs line-clamp-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 mb-2">
                  {service.shortDescription}
                </p>
                <span className="text-sky-400 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1 group-hover:text-sky-300">
                  Læs mere <ChevronRight className="h-3 w-3" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Advantages Section */}
      <section className="py-20 bg-slate-50/60 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {ADVANTAGES.map((adv, idx) => (
              <div
                key={idx}
                className="flex flex-col sm:flex-row gap-4 p-6 bg-white border border-slate-100 rounded-none shadow-sm hover:shadow-md hover:-translate-y-1 hover:border-sky-500/30 transition-all duration-300 group"
              >
                <div className="bg-sky-50 text-sky-500 group-hover:bg-sky-500 group-hover:text-white p-3.5 rounded-none h-fit w-fit shrink-0 transition-colors duration-300">
                  {getIconComponent(adv.icon)}
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-slate-900 text-base mb-1.5 uppercase tracking-wide group-hover:text-sky-500 transition-colors duration-300">
                    {adv.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {adv.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Split-Screen "Om os" Section */}
      <section className="relative bg-white overflow-hidden min-h-0 lg:min-h-[650px] flex flex-col lg:block">
        {/* Left Content Container */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            {/* Left Column: Text & Checklist */}
            <div className="lg:col-span-6 space-y-6">
              <span className="text-sky-500 font-bold text-xs uppercase tracking-wider">
                Om Skræp Vinduespuds
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight uppercase md:w-full">
                Din lokale og pålidelige partner på hele Fyn
              </h2>
              <p className="text-slate-600 text-base sm:text-lg leading-relaxed">
                Hos Skræp Vinduespuds går jeg op i det gode håndværk og den tætte kundekontakt. Jeg dækker hele Fyn – fra Odense til Svendborg, Middelfart, Nyborg og alle mindre byer derimellem. Jeg leverer altid et stribefrit og perfekt resultat med et smil.
              </p>

              {/* Checklist */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                {WHY_CHOOSE_US.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="bg-sky-50 text-sky-500 p-1 rounded-md shrink-0 mt-0.5">
                      <Check className="h-4 w-4" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 text-sm uppercase tracking-wide">{item.title}</h4>
                      <p className="text-slate-500 text-xs mt-0.5 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="pt-6">
                <Link
                  to="/om-os"
                  className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-600 text-white px-6 py-3 rounded-md text-xs font-bold uppercase tracking-wider transition-colors shadow-sm"
                >
                  Læs mere om os <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Full-Height Edge-to-Edge Image */}
        <div className="relative lg:absolute lg:right-0 lg:top-0 lg:bottom-0 w-full lg:w-1/2 h-[350px] sm:h-[450px] lg:h-full">
          <img
            src="https://assets.auxodev.dk/assets/66f352d5-c4b9-4e8b-8d46-eb7bb9ac1d49/a7628b4d-4b24-40e7-a19b-5d88216bb223.jpg"
            alt="Rent vindue og smuk udsigt"
            className="w-full h-full object-cover"
          />
          {/* Decorative overlay on image */}
          <div className="absolute inset-0 bg-gradient-to-r from-white via-transparent to-transparent hidden lg:block" />
          
          {/* Floating Badge */}
          <div className="absolute bottom-6 left-6 right-6 lg:left-12 lg:right-auto lg:max-w-md bg-slate-900/95 backdrop-blur-md p-6 rounded-none shadow-lg z-20 border border-white/10 flex items-center gap-4">
            <div className="bg-sky-500 text-white p-3 rounded-none shrink-0">
              <Sparkles className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs text-sky-400 font-bold uppercase tracking-wider">Vores garanti</p>
              <p className="text-sm font-bold text-white uppercase tracking-wide">100% tilfredshed eller pengene tilbage</p>
            </div>
          </div>
        </div>
      </section>

      {/* Four-Column Showcase (Flush Borderless Gallery Grid) */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
          <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-4">
            <div>
              <span className="text-sky-500 font-bold text-xs uppercase tracking-wider">
                Galleri
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight uppercase">
                Se mit udførte arbejde
              </h2>
            </div>
            <Link
              to="/galleri"
              className="inline-flex items-center gap-1.5 text-sky-500 font-bold text-xs uppercase tracking-wider hover:text-sky-600 transition-colors group"
            >
              Se hele galleriet
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>

        {loadingGallery ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-0">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((n, index) => (
              <div key={n} className={`bg-slate-200 aspect-square animate-pulse border border-slate-100 ${
                index >= 4 ? "hidden lg:block" : ""
              }`} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-0">
            {galleryItems.map((item, index) => (
              <div
                key={item.id}
                className={`relative group overflow-hidden aspect-square cursor-pointer bg-slate-900 ${
                  index >= 4 ? "hidden lg:block" : ""
                }`}
                onClick={() => setLightboxImage(item.image[0])}
              >
                <img
                  src={item.image[0]}
                  alt={item.title.da}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 opacity-90"
                />
                <div className="absolute inset-0 bg-slate-950/70 opacity-0 group-hover:opacity-100 transition-all duration-300 flex flex-col justify-end p-6 z-10">
                  <span className="text-xs font-bold text-sky-400 uppercase tracking-wider mb-1">
                    {item.category.replace("-", " ")}
                  </span>
                  <h3 className="text-white font-bold text-base uppercase tracking-wide mb-3">
                    {item.title.da}
                  </h3>
                  <div className="inline-flex items-center gap-1.5 text-white text-xs font-bold uppercase tracking-wider">
                    Forstør billede <Maximize2 className="h-3.5 w-3.5" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Reviews Section (Infinite Slider) */}
      <section className="py-24 bg-white overflow-hidden border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-sky-500 font-bold text-xs uppercase tracking-wider">
              Anmeldelser
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mt-2 tracking-tight uppercase">
              Hvad siger mine kunder?
            </h2>
          </div>
        </div>

        {/* Infinite Slider Container */}
        <div className="relative w-full">
          {/* Left/Right Gradients for fade effect */}
          <div className="absolute left-0 top-0 bottom-0 w-16 sm:w-32 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-16 sm:w-32 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />

          {/* Scrolling track */}
          <div className="flex gap-6 animate-infinite-scroll py-4 w-max">
            {/* Double the reviews to make it seamless */}
            {[...REVIEWS, ...REVIEWS].map((review, idx) => (
              <div
                key={idx}
                className="bg-slate-50 p-6 sm:p-8 rounded-md border border-slate-100 w-[290px] sm:w-[380px] shrink-0 flex flex-col justify-between shadow-sm hover:shadow-md transition-shadow"
              >
                <div>
                  <div className="flex items-center gap-1 text-amber-400 mb-4">
                    {[...Array(review.stars)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-current" />
                    ))}
                  </div>
                  <p className="text-slate-600 text-sm sm:text-base italic leading-relaxed mb-6">
                    &ldquo;{review.text}&rdquo;
                  </p>
                </div>
                <div className="flex items-center gap-3 pt-4 border-t border-slate-200/60">
                  <div className="bg-sky-500 text-white font-bold h-10 w-10 rounded-md flex items-center justify-center text-sm">
                    {review.name.charAt(0)}
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 text-sm uppercase tracking-wide">{review.name}</h4>
                    <p className="text-xs text-slate-500">{review.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Custom Infinite Scroll CSS */}
        <style>{`
          @keyframes infinite-scroll {
            0% { transform: translateX(0); }
            100% { transform: translateX(calc(-50% - 12px)); }
          }
          .animate-infinite-scroll {
            animation: infinite-scroll 40s linear infinite;
          }
          .animate-infinite-scroll:hover {
            animation-play-state: paused;
          }
        `}</style>
      </section>

      {/* Contact CTA Section over Dark Photographic Background Overlay */}
      <section className="relative py-24 overflow-hidden bg-slate-950">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/66f352d5-c4b9-4e8b-8d46-eb7bb9ac1d49/0cf23f6e-19fb-4e7f-9a92-823688f27365.jpg"
            alt="Dansk parcelhus med rene vinduer"
            className="w-full h-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/60 via-slate-950/40 to-slate-950/60" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            {/* Left Column: Info with White Text */}
            <div className="lg:col-span-5 space-y-6">
              <span className="text-sky-400 font-bold text-xs uppercase tracking-wider">
                Kontakt mig i dag
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight uppercase">
                Få et uforpligtende tilbud på din opgave
              </h2>
              <p className="text-slate-300 text-base sm:text-lg leading-relaxed">
                Udfyld formularen, eller ring til mig direkte. Jeg besvarer alle henvendelser hurtigt og rykker ud til opgaver på hele Fyn.
              </p>

              <div className="space-y-4 pt-4">
                {COMPANY_INFO.phone && (
                  <a
                    href={`tel:${COMPANY_INFO.phone.replace(/\s+/g, "")}`}
                    className="flex items-center gap-4 p-4 bg-white/5 rounded-md border border-white/10 hover:bg-white/10 transition-all group"
                  >
                    <div className="bg-sky-500 text-white p-3 rounded-md group-hover:bg-sky-600 transition-colors">
                      <Phone className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 font-bold uppercase tracking-wider">Ring direkte</p>
                      <p className="text-base sm:text-lg font-bold text-white">{COMPANY_INFO.phone}</p>
                    </div>
                  </a>
                )}
                {COMPANY_INFO.email && (
                  <a
                    href={`mailto:${COMPANY_INFO.email}`}
                    className="flex items-center gap-4 p-4 bg-white/5 rounded-md border border-white/10 hover:bg-white/10 transition-all group"
                  >
                    <div className="bg-sky-500 text-white p-3 rounded-md group-hover:bg-sky-600 transition-colors">
                      <Mail className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 font-bold uppercase tracking-wider">Skriv e-mail</p>
                      <p className="text-sm sm:text-lg font-bold text-white">{COMPANY_INFO.email}</p>
                    </div>
                  </a>
                )}
                <div className="flex items-center gap-4 p-4 bg-white/5 rounded-md border border-white/10">
                  <div className="bg-sky-500 text-white p-3 rounded-md">
                    <MapPin className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 font-bold uppercase tracking-wider">Dækningsområde</p>
                    <p className="text-base sm:text-lg font-bold text-white">{COMPANY_INFO.geoArea}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Glassmorphism Form */}
            <div className="lg:col-span-7">
              <ContactForm variant="glass" />
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div
          className="fixed inset-0 bg-slate-950/95 z-50 flex items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={() => setLightboxImage(null)}
        >
          <button
            className="absolute top-6 right-6 text-white bg-white/10 hover:bg-white/20 p-2 rounded-md transition-colors"
            onClick={() => setLightboxImage(null)}
          >
            <X className="h-6 w-6" />
          </button>
          <img
            src={lightboxImage}
            alt="Stor visning"
            className="max-w-full max-h-[90vh] object-contain rounded-md shadow-2xl animate-in zoom-in-95 duration-200"
          />
        </div>
      )}
    </div>
  );
}
